/**
 * TikTok video pre-flight from facts the editor can read off the file: pixel
 * size, frame rate and whether it has a sound track. Mora stores only the
 * duration and aspect ratio of a video, so the editor probes the rest in the
 * browser (`src/hooks/useVideoFacts.ts`). A value the browser could not read is
 * `null`, never guessed, and shows as "could not check" rather than as a pass.
 *
 * Limits (media transfer guide, read 2026-11-03): 360 to 4096 px per side;
 * 23 to 60 FPS. TikTok has no audio rule, so a silent video is a warning only.
 * https://developers.tiktok.com/doc/content-posting-api-media-transfer-guide
 */
export interface TikTokVideoFacts {
  width: number | null;
  height: number | null;
  /** Measured by sampling playback, so approximate. */
  fps: number | null;
  hasAudio: boolean | null;
}

export const TIKTOK_VIDEO_MIN_SIDE = 360;
export const TIKTOK_VIDEO_MAX_SIDE = 4096;
export const TIKTOK_VIDEO_MIN_FPS = 23;
export const TIKTOK_VIDEO_MAX_FPS = 60;
/** The sampled rate is an estimate; only a clear miss blocks. */
const FPS_TOLERANCE = 1.5;

export interface TikTokVideoFactIssue {
  code:
    | 'tiktok_video_dimensions_unsupported'
    | 'tiktok_video_fps_unsupported'
    | 'tiktok_video_silent'
    | 'tiktok_video_unchecked';
  message: string;
  severity: 'blocked' | 'warning';
}

export function evaluateTikTokVideoFacts(
  facts: TikTokVideoFacts | null | undefined,
): TikTokVideoFactIssue[] {
  if (!facts) return [];
  const issues: TikTokVideoFactIssue[] = [];
  const { width, height, fps, hasAudio } = facts;

  if (width !== null && height !== null) {
    const shortest = Math.min(width, height);
    const longest = Math.max(width, height);
    if (shortest < TIKTOK_VIDEO_MIN_SIDE || longest > TIKTOK_VIDEO_MAX_SIDE) {
      issues.push({
        code: 'tiktok_video_dimensions_unsupported',
        message: `TikTok accepts videos from ${TIKTOK_VIDEO_MIN_SIDE} to ${TIKTOK_VIDEO_MAX_SIDE} pixels on each side. This one is ${width} by ${height}.`,
        severity: 'blocked',
      });
    }
  }

  if (fps !== null) {
    if (fps < TIKTOK_VIDEO_MIN_FPS - FPS_TOLERANCE || fps > TIKTOK_VIDEO_MAX_FPS + FPS_TOLERANCE) {
      issues.push({
        code: 'tiktok_video_fps_unsupported',
        message: `TikTok accepts ${TIKTOK_VIDEO_MIN_FPS} to ${TIKTOK_VIDEO_MAX_FPS} frames per second. This video runs at about ${Math.round(fps)}.`,
        severity: 'blocked',
      });
    }
  }

  if (hasAudio === false) {
    issues.push({
      code: 'tiktok_video_silent',
      message: 'This video has no sound. TikTok will post it silent.',
      severity: 'warning',
    });
  }

  const unknown: string[] = [];
  if (width === null || height === null) unknown.push('size');
  if (fps === null) unknown.push('frame rate');
  if (hasAudio === null) unknown.push('sound');
  if (unknown.length > 0) {
    issues.push({
      code: 'tiktok_video_unchecked',
      message: `Mora could not check this video's ${unknown.join(', ')} before sending. TikTok may still reject it.`,
      severity: 'warning',
    });
  }
  return issues;
}
